<template>
  <v-dialog persistent v-model="isOpen" max-width="fit-content">
    <template v-if="!isInitialized">
      <v-card class="d-flex" height="300" width="500">
        <v-progress-circular
          class="ma-auto"
          color="primary"
          :width="3"
          indeterminate
        />
      </v-card>
    </template>
    <template v-else>
      <CreateTaskForBusiness
        v-if="currentStep === allSteps.CREATE_TASK_FOR_BUSINESS"
        :taskName="name"
        :selectedBusiness="business"
        :hideBackButton="true"
        v-on:updateSelectedBusiness="setSelectedBusiness"
        v-on:updateTaskName="setTaskName"
        v-on:backAction="handlePreviousStep"
        v-on:cancelAction="handleCancel"
        v-on:nextAction="handleNextStep"
      />
      <SelectTaskImportSession
        v-if="currentStep === allSteps.SELECT_TASK_IMPORT_SESSION"
        :businessId="business.id"
        :importSession="importSession"
        v-on:updateImportSession="setImportSession"
        v-on:backAction="handlePreviousStep"
        v-on:cancelAction="handleCancel"
        v-on:nextAction="handleNextStep"
      />
      <SelectTaskImportRequest
        v-if="currentStep === allSteps.SELECT_TASK_IMPORT_REQUEST"
        :importSessionId="importSession.id"
        :importRequest="importRequest"
        v-on:updateImportRequest="setImportRequest"
        v-on:backAction="handlePreviousStep"
        v-on:cancelAction="handleCancel"
        v-on:nextAction="handleNextStep"
      />
      <UploadTaskFiles
        v-if="currentStep === allSteps.UPLOAD_TASK_FILES"
        :importRequestId="importRequest && importRequest.id"
        :recentResultId="recentResultId"
        :useRecentResult="useRecentResult"
        :useSessionInventoryFile="useSessionInventoryFile"
        :sessionInventoryFilePath="sessionInventoryFilePath"
        :selectedInputFile="selectedInputFile"
        :selectedInputFileFormat="selectedInputFileFormat"
        :selectedHistoryFile="selectedHistoryFile"
        :selectedPreviousRunFile="selectedPreviousRunFile"
        :selectedInventoryFile="selectedInventoryFile"
        v-on:uploadChange="handleUploadChange"
        v-on:selectedInputFileFormatChange="handleSelectedInputFileFormatChange"
        v-on:useSessionInventoryFileChange="handleSessionInventoryFileChange"
        v-on:useRecentResultChange="handleUseRecentResultChange"
        v-on:backAction="handlePreviousStep"
        v-on:cancelAction="handleCancel"
        v-on:nextAction="handleNextStep"
      />
      <NewTaskOverview
        v-if="currentStep === allSteps.TASK_OVERVIEW"
        nextButtonText="Create"
        :name="name"
        :business="business"
        :importSession="importSession"
        :importRequest="importRequest"
        :recentResultId="recentResultId"
        :useRecentResult="useRecentResult"
        :sessionInventoryFilePath="sessionInventoryFilePath"
        :useSessionInventoryFile="useSessionInventoryFile"
        :selectedInputFile="selectedInputFile"
        :selectedInputFileFormat="selectedInputFileFormat"
        :selectedHistoryFile="selectedHistoryFile"
        :selectedPreviousRunFile="selectedPreviousRunFile"
        :selectedInventoryFile="selectedInventoryFile"
        :nextButtonIsLoading="isLoading"
        v-on:changeStep="handleChangeStep"
        v-on:backAction="handlePreviousStep"
        v-on:cancelAction="handleCancel"
        v-on:nextAction="onCreateNewTask"
      />
      <AdvancedTaskOptions
        v-if="currentStep === allSteps.ADVANCED_TASK_OPTIONS && !isLoading"
        :taskOptions="taskOptions"
        v-on:taskOptionsChange="handleTaskOptionChange"
        v-on:backAction="handlePreviousStep"
      />
    </template>
  </v-dialog>
</template>

<script>
import CreateTaskForBusiness from './steps/CreateTaskForBusiness.vue';
import SelectTaskImportRequest from './steps/SelectTaskImportRequest.vue';
import SelectTaskImportSession from './steps/SelectTaskImportSession.vue';
import UploadTaskFiles from './steps/UploadTaskFiles.vue';
import NewTaskOverview from './steps/NewTaskOverview.vue';
import { businessApiV2, tasksApiV2 } from '@/api/v2';

import * as _ from 'lodash';
import CONFIG from '@/config';
import { FileUploadType } from '@/constants';
import {
  defaultTask,
  TaskCreationSteps,
  OrderedTaskCreationSteps
} from '@/constants/task';
import { createTaskFormData } from './utils';
import AdvancedTaskOptions from './steps/AdvancedTaskOptions.vue';

export default {
  name: 'CreateMatcherTaskV2',
  components: {
    CreateTaskForBusiness,
    SelectTaskImportSession,
    SelectTaskImportRequest,
    UploadTaskFiles,
    NewTaskOverview,
    AdvancedTaskOptions
  },
  data() {
    return {
      isInitialized: false,
      isLoading: false,
      allSteps: TaskCreationSteps,
      steps: OrderedTaskCreationSteps,
      currentStepIndex: 0,
      isOpen: false,
      selectedEnv: CONFIG.ENV,
      // See default task definition for values
      ..._.cloneDeep(defaultTask)
    };
  },
  computed: {
    currentStep() {
      return this.steps[this.currentStepIndex];
    }
  },
  watch: {
    business() {
      // Reset these values when the business changes
      this.importSession = undefined;
      this.importRequest = undefined;
    },
    importSession() {
      // Reset these values when the business changes
      this.importRequest = undefined;
    },
    isOpen() {
      // Reset task data on close
      if (!this.isOpen) {
        const newDefaultTask = _.cloneDeep(defaultTask);
        this.currentStepIndex = 0;
        Object.keys(newDefaultTask).forEach((key) => {
          this[key] = newDefaultTask[key];
        });
      }
    }
  },
  methods: {
    async open(task) {
      this.isInitialized = !task;
      this.isOpen = true;
      if (task) {
        this.currentStepIndex = this.steps.indexOf(this.allSteps.TASK_OVERVIEW);
        await this.copyExistingTask(task);
        this.isInitialized = true;
      }
    },
    async copyExistingTask(task) {
      const existingTask = _.cloneDeep(task);
      this.name = existingTask.name;
      this.business = existingTask.business;
      /**
       * The business id the matcher saves is the unobfuscated id where as the rhapsody business api response ids contain
       * the obfuscated id. This matters for display purposes since we mostly work with the obfuscated id.
       */
      this.business.id = existingTask.business.rhapsody_id;

      // The V2 Matcher task only supports one input file
      this.selectedInputFile = existingTask.files.find(
        (file) => file.file_type === FileUploadType.UPLOADS
      );
      this.selectedInputFileFormat =
        existingTask.params.inputFilesParams[0]?.type;

      // File properties
      existingTask.files.forEach((file) => {
        switch (file.file_type) {
          case FileUploadType.SALES_HISTORIES:
            this.selectedHistoryFile = file;
            break;
          case FileUploadType.PREVIOUS_RUNS:
            this.selectedPreviousRunFile = file;
            break;
          case FileUploadType.INVENTORY_FILES:
            this.selectedInventoryFile = file;
            break;
        }
      });

      this.taskOptions = {
        ...existingTask.params,
        ...JSON.parse(existingTask.params.context)
      };

      // Fetch the matcher task session/request details
      this.importSession = await businessApiV2.fetchBusinessSession(
        existingTask.rhapsody_enterprise_session_id,
        this.selectedEnv
      );

      if (existingTask.rhapsody_completed_import_request_id) {
        this.importRequest = await businessApiV2.fetchSessionImportRequest(
          existingTask.rhapsody_completed_import_request_id,
          this.selectedEnv
        );
      }
    },
    handleChangeStep(step) {
      if (this.allSteps[step]) {
        this.currentStepIndex = this.steps.indexOf(step);
      }
    },
    handleNextStep() {
      if (this.currentStepIndex < this.steps.length) {
        this.currentStepIndex++;
      } else {
        throw new Error(
          `No step defined for step index ${this.currentStepIndex + 1}`
        );
      }
    },
    handleCancel() {
      this.isOpen = false;
    },
    handlePreviousStep() {
      if (this.currentStepIndex > 0) {
        this.currentStepIndex--;
      } else {
        throw new Error(
          `No step defined for step index ${this.currentStepIndex - 1}`
        );
      }
    },
    setTaskName(taskName) {
      this.name = taskName;
    },
    setSelectedBusiness(business) {
      this.business = business;

      if (this.business) {
        this.taskOptions.officialname = this.business.name;
        this.taskOptions.clinic = _.snakeCase(this.business.name);
      }
    },
    setImportSession(importSession) {
      this.importSession = importSession;
    },
    setImportRequest(importRequest) {
      this.importRequest = importRequest;
    },
    handleUploadChange({ type, upload }) {
      switch (type) {
        case FileUploadType.UPLOADS:
          this.selectedInputFile = upload;
          break;
        case FileUploadType.SALES_HISTORIES:
          this.selectedHistoryFile = upload;
          break;
        case FileUploadType.PREVIOUS_RUNS:
          this.selectedPreviousRunFile = upload;
          break;
        case FileUploadType.INVENTORY_FILES:
          this.selectedInventoryFile = upload;
          break;
      }
    },
    handleSessionInventoryFileChange(path, isToggled) {
      this.sessionInventoryFilePath = path;
      this.useSessionInventoryFile = isToggled;
    },
    handleSelectedInputFileFormatChange(format) {
      this.selectedInputFileFormat = format;
    },
    handleUseRecentResultChange(isToggled) {
      this.useRecentResult = isToggled;
    },
    handleTaskOptionChange(optionKey, optionValue) {
      this.taskOptions = { ...this.taskOptions, [optionKey]: optionValue };
    },
    async onCreateNewTask() {
      try {
        this.isLoading = true;
        const formData = createTaskFormData(this);
        await tasksApiV2.createTask(formData);
        this.isOpen = false;
      } catch (e) {
        this.$notify({
          type: 'error',
          title: 'Error',
          text:
            'Error creating matcher task' + (e?.message ? `: ${e.message}` : '')
        });
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>
